import React, { useEffect, useRef } from 'react';
import { Container, Nav } from 'react-bootstrap';
import { NavLink, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './components/Home';
import ApiTest from './components/ApiTest';
import Login from './components/Login';
import Logout from './components/Logout';
import PrivateRoute from './services/PrivateRoute';
import AuthService from './services/AuthService';
import StudentWorkspace, { useImpersonation, useCanViewUndergraduate, useCanViewGraduate, useCanEvaluateUndergraduate, useCanViewUndergraduateApplications, useCanViewGraduateStaff } from './components/StudentWorkspace';
import Impersonate from './components/Impersonate';
import UndergraduateOverview from './components/UndergraduateOverview';
import UndergraduateAdmin from './components/UndergraduateAdmin';
import AdminApplicationDetails from './components/AdminApplicationDetails';
import ViewerApplicationDetails from './components/ViewerApplicationDetails';
import ManageAccess from './components/ManageAccess';
import EmailTemplates from './components/EmailTemplates';
import GraduateOverview from './components/GraduateOverview';
import GraduateApplicationsAdmin from './components/GraduateApplicationsAdmin';
import GraduateApplicationAdminEditor from './components/GraduateApplicationAdminEditor';
import GraduateApplicationStaffDetail from './components/GraduateApplicationStaffDetail';

const routeTitles: Record<string, string> = {
  '/': 'Home',
  '/login': 'Sign In',
  '/logout': 'Signing Out',
  '/admin/undergraduate': 'Undergraduate Applications',
  '/admin/graduate': 'Graduate Applications',
  '/admin/access': 'Manage Access',
  '/admin/email-templates': 'Email Templates',
  '/undergraduate': 'Undergraduate overview',
  '/graduate': 'My Applications',
  '/impersonate': 'Impersonate User',
  '/api-test': 'API Test',
  '/GraduationApplicationAdmin/Undergraduate/UnderGrad': 'Undergraduate Applications',
  '/GraduationApplicationAdmin/Undergraduate/UGViewer': 'Undergraduate Applications',
};

function pageTitle(pathname: string) {
  if (/^\/admin\/graduate\/\d+\/edit$/.test(pathname)) return 'Edit graduate application';
  if (pathname.startsWith('/admin/graduate/')) return 'Graduate application details';
  if (pathname.startsWith('/admin/undergraduate/') || pathname.endsWith('/ApplicationView')) return 'Undergraduate application details';
  return routeTitles[pathname] || 'Graduation Application';
}

function focusMainContent(main: HTMLElement) {
  const target = main.querySelector<HTMLElement>('h1') || main;
  target.tabIndex = -1;
  target.focus();
}

function RouteAccessibility() {
  const { pathname } = useLocation();
  const previousPathname = useRef(pathname);
  const title = `${pageTitle(pathname)} – Minnesota State University, Mankato`;

  useEffect(() => {
    document.title = title;
    const hasNavigated = previousPathname.current !== pathname;
    previousPathname.current = pathname;
    if (!hasNavigated) return;

    const main = document.getElementById('main-content');
    if (!main) return;
    focusMainContent(main);
    if (main.querySelector('h1')) return;

    // Some routes show a loading state before their heading is available.
    // Move from the main landmark to that heading only while focus is still there.
    const observer = new MutationObserver(() => {
      if (document.activeElement !== main) {
        observer.disconnect();
      } else if (main.querySelector('h1')) {
        focusMainContent(main);
        observer.disconnect();
      }
    });
    observer.observe(main, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [pathname, title]);

  return <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">{title}</div>;
}

function Navigation() {
  const impersonation = useImpersonation();
  const canViewUndergraduate = useCanViewUndergraduate();
  const canViewGraduate = useCanViewGraduate();
  const canViewApplications = useCanViewUndergraduateApplications();
  const canViewGraduateStaff = useCanViewGraduateStaff();
  if (!AuthService.isTokenValid()) return null;
  return <Nav as="nav" aria-label="Primary application" variant="tabs" className="top-tabs">
    {!canViewUndergraduate && <Nav.Link as={NavLink} to="/" end>Home</Nav.Link>}
    {canViewApplications && <Nav.Link as={NavLink} to="/admin/undergraduate">Undergraduate Applications</Nav.Link>}
    {canViewGraduateStaff && <Nav.Link as={NavLink} to="/admin/graduate">Graduate Applications</Nav.Link>}
    {canViewUndergraduate && <Nav.Link as={NavLink} to="/undergraduate">Undergraduate overview</Nav.Link>}
    {canViewGraduate && <Nav.Link as={NavLink} to="/graduate">My Applications</Nav.Link>}
    {!impersonation && AuthService.getCurrentToken()?.canImpersonate && <Nav.Link as={NavLink} to="/impersonate">Impersonate User</Nav.Link>}
    {!impersonation && AuthService.getCurrentToken()?.canImpersonate && <Nav.Link as={NavLink} to="/admin/access">Manage Access</Nav.Link>}
    {!impersonation && AuthService.getCurrentToken()?.canImpersonate && <Nav.Link as={NavLink} to="/admin/email-templates">Email Templates</Nav.Link>}
  </Nav>;
}

export default function App() {
  const location = useLocation();
  const canViewUndergraduate = useCanViewUndergraduate();
  const canViewGraduate = useCanViewGraduate();
  const canEvaluateUndergraduate = useCanEvaluateUndergraduate();
  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content" onClick={event => {
        event.preventDefault();
        const main = document.getElementById('main-content');
        if (main) focusMainContent(main);
      }}>Skip to main content</a>
      <Header />
      <RouteAccessibility />
      {!['/login', '/logout'].includes(location.pathname) && <Container><Navigation /></Container>}
      <Container as="main" id="main-content" tabIndex={-1} className="app-main">
        <div className="mt-3"><Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route element={<PrivateRoute />}>
          <Route element={<StudentWorkspace><Outlet /></StudentWorkspace>}>
            <Route path="/" element={<Home />} />
            <Route path="/admin/undergraduate" element={<UndergraduateAdmin />} />
            <Route path="/admin/graduate" element={<GraduateApplicationsAdmin />} />
            <Route path="/admin/graduate/:id/edit" element={<GraduateApplicationAdminEditor />} />
            <Route path="/admin/graduate/:id" element={<GraduateApplicationStaffDetail />} />
            <Route path="/admin/access" element={<ManageAccess />} />
            <Route path="/admin/email-templates" element={<EmailTemplates />} />
            <Route path="/admin/undergraduate/:id" element={canEvaluateUndergraduate ? <AdminApplicationDetails /> : <ViewerApplicationDetails />} />
            <Route path="/GraduationApplicationAdmin/Undergraduate/UnderGrad/ApplicationView" element={<AdminApplicationDetails />} />
            <Route path="/GraduationApplicationAdmin/Undergraduate/UnderGrad" element={<UndergraduateAdmin />} />
            <Route path="/GraduationApplicationAdmin/Undergraduate/UGViewer/ApplicationView" element={<ViewerApplicationDetails />} />
            <Route path="/GraduationApplicationAdmin/Undergraduate/UGViewer" element={<UndergraduateAdmin />} />
            <Route path="/api-test" element={<ApiTest />} />
            <Route path="/impersonate" element={<Impersonate />} />
            <Route path="/impersonate/evaluator" element={<Navigate to="/impersonate" replace />} />
            <Route path="/impersonate/viewer" element={<Navigate to="/impersonate" replace />} />
            <Route path="/undergraduate" element={canViewUndergraduate ? <UndergraduateOverview /> : <Navigate to="/" replace />} />
            <Route path="/graduate" element={canViewGraduate ? <GraduateOverview /> : <Navigate to="/" replace />} />
          </Route>
          </Route>
          <Route path="*" element={<Navigate to={AuthService.isTokenValid() ? '/' : '/login'} replace />} />
        </Routes></div>
      </Container>
      <Footer />
    </div>
  );
}

